import { useState } from 'react';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Zoom from '@mui/material/Zoom';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ErrorIcon from '@mui/icons-material/Error';
import { alpha } from '@mui/material/styles';
import { useSurveyTranslation } from '@/i18n/hooks/useTypedTranslation';
import type { DraftSaveStatus } from '@/hooks/useDraftManager';

interface FloatingSaveButtonProps {
  /** Whether there are unsaved changes to show the button */
  hasUnsavedChanges: boolean;
  /** Current save status */
  saveStatus: DraftSaveStatus;
  /** Last saved timestamp */
  lastSavedAt: Date | null;
  /** Callback when save is clicked */
  onSave: () => Promise<void>;
  /** Position from bottom */
  bottom?: number;
  /** Position from right */
  right?: number;
}

export function FloatingSaveButton({
  hasUnsavedChanges,
  saveStatus,
  lastSavedAt,
  onSave,
  bottom = 24,
  right = 24,
}: FloatingSaveButtonProps) {
  const { t } = useSurveyTranslation();
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  const isLoading = saveStatus === 'saving';
  const isOffline = saveStatus === 'offline';
  const isError = saveStatus === 'error';
  const isSaved = saveStatus === 'saved';

  // Show button when there are unsaved changes OR when showing save status
  const showButton = hasUnsavedChanges || isLoading || isSaved;

  const handleClick = async () => {
    if (isLoading || isOffline) return;

    try {
      await onSave();
      setShowSuccessSnackbar(true);
    } catch {
      setShowErrorSnackbar(true);
    }
  };

  const getIcon = () => {
    if (isLoading) {
      return <CircularProgress size={24} color="inherit" />;
    }
    if (isSaved) {
      return <CheckIcon />;
    }
    if (isOffline) {
      return <CloudOffIcon />;
    }
    if (isError) {
      return <ErrorIcon />;
    }
    return <SaveIcon />;
  };

  const getColor = (): 'primary' | 'success' | 'error' | 'default' => {
    if (isSaved) return 'success';
    if (isError) return 'error';
    if (isOffline) return 'default';
    return 'primary';
  };

  const getTooltip = () => {
    if (isLoading) return t('draft.status.saving');
    if (isSaved) return t('draft.status.saved');
    if (isOffline) return t('draft.status.offline');
    if (isError) return t('draft.status.error');
    return t('draft.saveProgress');
  };

  const formatLastSaved = () => {
    if (!lastSavedAt) return null;
    const now = new Date();
    const diff = now.getTime() - lastSavedAt.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return t('draft.savedJustNow');
    if (minutes === 1) return t('draft.savedMinuteAgo');
    return t('draft.savedMinutesAgo', { count: minutes });
  };

  return (
    <>
      <Zoom in={showButton}>
        <Box
          sx={{
            position: 'fixed',
            bottom,
            right,
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {/* Last saved indicator - shows above the button */}
          {lastSavedAt && !hasUnsavedChanges && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.7rem',
                boxShadow: 1,
              }}
            >
              {formatLastSaved()}
            </Typography>
          )}

          {/* Unsaved changes badge */}
          {hasUnsavedChanges && !isLoading && !isSaved && (
            <Typography
              variant="caption"
              sx={{
                color: 'warning.main',
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'warning.light',
              }}
            >
              {t('draft.unsavedChanges')}
            </Typography>
          )}

          <Tooltip title={getTooltip()} placement="left">
            <Fab
              color={getColor()}
              onClick={handleClick}
              disabled={isLoading || isOffline}
              sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: isLoading ? 'none' : 'scale(1.05)',
                },
                // Pulse animation when there are unsaved changes
                ...(hasUnsavedChanges && !isLoading && !isSaved && {
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)',
                    },
                    '70%': {
                      boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                    },
                  },
                }),
              }}
            >
              {getIcon()}
            </Fab>
          </Tooltip>
        </Box>
      </Zoom>

      {/* Success snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('draft.savedSuccessfully')}
        </Alert>
      </Snackbar>

      {/* Error snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={5000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowErrorSnackbar(false)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('draft.saveFailed')}
        </Alert>
      </Snackbar>
    </>
  );
}

export default FloatingSaveButton;
